"use client";

import { ArrowRight, FileCheck, Gauge, ShieldCheck, Users } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Pillars() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const cards = [{
    icon: FileCheck,
    title: "Precision Planning",
    body: "Detailed project roadmaps that eliminate ambiguity, ensuring every milestone is met with absolute clarity and professional rigor.",
    proof: "Standardized delivery framework"
  }, {
    icon: Gauge,
    title: "Accelerated Execution",
    body: "Optimized workflows designed to reduce turnaround times, allowing your team to focus on high-impact strategic initiatives.",
    proof: "40% faster project completion"
  }, {
    icon: ShieldCheck,
    title: "Quality Assurance",
    body: "Rigorous testing and validation protocols that safeguard your operations against errors and ensure consistent performance.",
    proof: "Zero-defect policy enforced"
  }, {
    icon: Users,
    title: "Expert Collaboration",
    body: "Dedicated specialists who integrate seamlessly with your internal teams to provide actionable insights and technical support.",
    proof: "10+ yrs industry expertise"
  }];
  return <section data-section-id="15851" id="website-pillars" className="bg-background text-foreground py-24 md:py-32 font-default">
      <div className="mx-auto max-w-[1240px] px-5 sm:px-8 md:px-14">
        {}
        <div className="h-px w-full bg-border" />

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-10">
          {}
          <div className="lg:col-span-4">
            <div ref={leftRef} className="lg:sticky lg:top-24 transition-all duration-[520ms] ease-out" style={{
            transform: leftVisible ? "translateY(0)" : "translateY(16px)",
            opacity: leftVisible ? 1 : 0
          }}>
              <p className="text-[13px] font-medium uppercase text-muted-foreground" style={{
              letterSpacing: "0.14em"
            }}>
                {"Our Core Pillars"}
              </p>

              <h2 className="mt-6 font-serif text-foreground" style={{
              fontSize: "clamp(2rem, 3.6vw, 3.25rem)",
              fontWeight: 400,
              letterSpacing: "-0.018em",
              lineHeight: 1.1
            }}>
                {"Engineered for "}
                <span className="relative italic">
                  {"excellence"}
                  <span aria-hidden="true" className="absolute left-0 right-0 bg-secondary" style={{
                  height: "1px",
                  bottom: "-4px"
                }} />
                </span>
                {" in every engagement."}
              </h2>

              <p className="mt-8 text-foreground/85" style={{
              fontSize: "clamp(1.0625rem, 1.2vw, 1.1875rem)",
              lineHeight: 1.6,
              maxWidth: "32ch"
            }}>
                We provide the structural foundation your business needs to scale effectively. By combining technical precision with deep industry knowledge, we turn complex operational challenges into sustainable growth opportunities.
              </p>

              <Link to="#" className="group mt-10 inline-flex items-center gap-2 text-foreground">
                <span className="relative pb-1">
                  {"Explore our services"}
                  <span aria-hidden="true" className="absolute left-0 bottom-0 h-px bg-border transition-all duration-[240ms] ease-out group-hover:w-full group-hover:bg-secondary" style={{
                  width: "60%"
                }} />
                </span>
                <ArrowRight className="size-4 transition-transform duration-[240ms] ease-out group-hover:translate-x-1" strokeWidth={1.25} />
              </Link>
            </div>
          </div>

          {}
          <div className="lg:col-span-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {cards.map((card, i) => {
              const Icon = card.icon;
              return <AdvantageCard key={card.title} data-index={i} index={i} icon={Icon} title={card.title} body={card.body} proof={card.proof} />;
            })}
            </div>
          </div>
        </div>
      </div>
    </section>;
}
function AdvantageCard({
  index,
  icon: Icon,
  title,
  body,
  proof,
  ...rest
}: {
  index: number;
  icon: React.ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  title: string;
  body: string;
  proof: string;
  [key: string]: unknown;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <div ref={ref} {...rest} className="group relative bg-card p-8 border border-border transition-all duration-[280ms] ease-out hover:shadow-lg" style={{
    borderRadius: "0rem",
    transform: isVisible ? "translateY(0)" : "translateY(16px)",
    opacity: isVisible ? 1 : 0,
    transitionDelay: `${index * 80}ms`,
    transitionProperty: "transform, opacity, box-shadow",
    transitionDuration: "520ms, 520ms, 280ms"
  }}>
      <Icon className="size-6 text-foreground" strokeWidth={1.25} />

      <h3 className="relative mt-6 inline-block text-foreground" style={{
      fontFamily: "var(--font-sans)",
      fontSize: "clamp(1.25rem, 1.6vw, 1.5rem)",
      fontWeight: 500,
      letterSpacing: "-0.01em",
      lineHeight: 1.25
    }}>
        {title}
        <span aria-hidden="true" className="absolute left-0 bg-secondary transition-all duration-[220ms] ease-out group-hover:w-full" style={{
        height: "1px",
        bottom: "-4px",
        width: "0%"
      }} />
      </h3>

      <p className="mt-4 text-foreground/85" style={{
      fontSize: "clamp(0.9375rem, 1vw, 1rem)",
      lineHeight: 1.62
    }}>
        {body}
      </p>

      <p className="mt-6 text-muted-foreground" style={{
      fontFamily: "var(--font-mono)",
      fontSize: "0.8125rem",
      letterSpacing: "0.02em",
      lineHeight: 1.3
    }}>
        {proof}
      </p>
    </div>;
}